/**
 * ConnectionCard (Phase 2, §7.5; FinTS divergence Phase 4 §7.3) — one bank
 * connection with its status, consent countdown, discovered-account list and
 * lifecycle actions.
 *
 * Enable-Banking actions:
 *  - "Jetzt synchronisieren" (`useSyncConnection`, disabled while running; on a
 *    `rate_limited` error the toast surfaces the backend Retry-After message);
 *  - "Neu verbinden" (only when `needs_reauth`) → re-authorize the SAME
 *    connection via `reauth_connection_id`, then redirect to the bank's SCA;
 *  - "Trennen" (`useRevokeConnection`, behind a ConfirmDialog).
 *
 * FinTS actions (`connection.provider === 'fints'`) diverge — no
 * `window.location` redirect anywhere in this branch:
 *  - "Jetzt synchronisieren" → `useFintsSync`; a `challenge` in the response
 *    opens `TanWaitPanel` in a `Modal` (a `run` means it finished with no TAN).
 *  - "Neu verbinden" (`needs_reauth` = `expired` TAN/SCA or `error` rejected
 *    PIN) → a small `Modal` with an optional new-PIN field, submit →
 *    `useFintsReauth`; a `challenge` swaps the same modal to `TanWaitPanel`.
 *  - "Trennen" is unchanged (the backend skips the EB `delete_session` call
 *    for FinTS rows — no remote session to revoke).
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { RefreshCw, RotateCcw, Unlink } from 'lucide-react';
import { Button } from '../../../components/ui/Button.tsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.tsx';
import { Field } from '../../../components/ui/Field.tsx';
import { Input } from '../../../components/ui/Input.tsx';
import { Modal } from '../../../components/ui/Modal.tsx';
import { useToast } from '../../../context/ToastContext.tsx';
import { formatDateTime } from '../../../lib/format.ts';
import {
  useAuthorizeConnection,
  useRevokeConnection,
  useSyncConnection,
} from '../../../lib/hooks/useConnectionMutations.ts';
import { useFintsReauth, useFintsSync } from '../../../lib/hooks/useFints.ts';
import { PROVIDER, SYNC_RUN_SENTENCE } from '../../../lib/labels.ts';
import type { ConnectionOut, FintsChallengeOut } from '../../../lib/types.ts';
import { StatusBadge } from './StatusBadge.tsx';
import { ConsentCountdown } from './ConsentCountdown.tsx';
import { AccountMappingRow } from './AccountMappingRow.tsx';
import { TanWaitPanel } from './TanWaitPanel.tsx';

export interface ConnectionCardProps {
  connection: ConnectionOut;
  /** Link the header to the detail page (list view); off on the detail page. */
  linkToDetail?: boolean;
}

export function ConnectionCard({ connection, linkToDetail = true }: ConnectionCardProps) {
  const { toast } = useToast();
  const isFints = connection.provider === 'fints';
  const sync = useSyncConnection();
  const revoke = useRevokeConnection();
  const authorize = useAuthorizeConnection();
  const fintsSync = useFintsSync();
  const fintsReauth = useFintsReauth();
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [fintsSyncChallenge, setFintsSyncChallenge] = useState<FintsChallengeOut | null>(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [reauthPin, setReauthPin] = useState('');
  const [reauthChallenge, setReauthChallenge] = useState<FintsChallengeOut | null>(null);

  function runSync() {
    sync.mutate(
      { id: connection.id, body: { trigger: 'manual' } },
      {
        onSuccess: (run) => {
          toast(SYNC_RUN_SENTENCE[run.status], {
            variant: run.status === 'success' ? 'success' : 'info',
          });
        },
        onError: (err) => toast(err.message, { variant: 'error' }),
      },
    );
  }

  function runFintsSync() {
    fintsSync.mutate(
      { id: connection.id, body: { trigger: 'manual' } },
      {
        onSuccess: (data) => {
          if (data.challenge) {
            setFintsSyncChallenge(data.challenge);
          } else if (data.run) {
            toast(SYNC_RUN_SENTENCE[data.run.status], {
              variant: data.run.status === 'success' ? 'success' : 'info',
            });
          }
        },
        onError: (err) => toast(err.message, { variant: 'error' }),
      },
    );
  }

  function reauth() {
    authorize.mutate(
      {
        aspsp_name: connection.aspsp_name,
        aspsp_country: connection.aspsp_country,
        reauth_connection_id: connection.id,
      },
      {
        onSuccess: (data) => {
          window.location.href = data.authorization_url;
        },
        onError: (err) => toast(err.message, { variant: 'error' }),
      },
    );
  }

  function submitFintsReauth() {
    fintsReauth.mutate(
      { id: connection.id, body: { pin: reauthPin.trim() || null } },
      {
        onSuccess: (data) => {
          // Clear the PIN from component state immediately after submit.
          setReauthPin('');
          if (data.challenge) {
            setReauthChallenge(data.challenge);
          } else {
            setReauthOpen(false);
            toast('Bankverbindung erneuert.', { variant: 'success' });
          }
        },
        onError: (err) => {
          setReauthPin('');
          toast(err.message, { variant: 'error' });
        },
      },
    );
  }

  function doRevoke() {
    revoke.mutate(connection.id, {
      onSuccess: () => {
        setConfirmRevoke(false);
        toast('Bankverbindung getrennt.', { variant: 'success' });
      },
      onError: (err) => {
        setConfirmRevoke(false);
        toast(err.message, { variant: 'error' });
      },
    });
  }

  const canSync = connection.status === 'active';

  return (
    // Flat surface (DESIGN.md §1): no card chrome — type and whitespace structure it.
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {linkToDetail ? (
              <RouterLink
                to={`/bankverbindungen/${connection.id}`}
                className="truncate text-base font-semibold text-slate-900 transition-colors duration-150 hover:text-sky-600 dark:text-slate-100 dark:hover:text-sky-400"
              >
                {connection.bank_label}
              </RouterLink>
            ) : (
              <h2 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
                {connection.bank_label}
              </h2>
            )}
            <StatusBadge status={connection.status} />
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {PROVIDER[connection.provider]}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <ConsentCountdown days={connection.consent_days_remaining} />
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {connection.last_sync_at
                ? `Zuletzt abgeglichen ${formatDateTime(connection.last_sync_at)}`
                : 'Noch nicht abgeglichen'}
            </span>
          </div>
        </div>
      </div>

      {connection.accounts.length > 0 ? (
        <ul className="mt-2 divide-y divide-slate-100 dark:divide-slate-800/70">
          {connection.accounts.map((account) => (
            <AccountMappingRow key={account.account_id} account={account} />
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-slate-400 dark:text-slate-500">
          Noch keine Konten entdeckt.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 dark:border-slate-800/70">
        {connection.needs_reauth ? (
          isFints ? (
            <Button size="sm" onClick={() => setReauthOpen(true)} disabled={fintsReauth.isPending}>
              <RotateCcw className="h-4 w-4" />
              Neu verbinden
            </Button>
          ) : (
            <Button size="sm" onClick={reauth} disabled={authorize.isPending}>
              <RotateCcw className="h-4 w-4" />
              {authorize.isPending ? 'Weiterleitung …' : 'Neu verbinden'}
            </Button>
          )
        ) : isFints ? (
          <Button size="sm" onClick={runFintsSync} disabled={!canSync || fintsSync.isPending}>
            <RefreshCw className={`h-4 w-4 ${fintsSync.isPending ? 'animate-spin' : ''}`} />
            {fintsSync.isPending ? 'Wird abgeglichen …' : 'Jetzt synchronisieren'}
          </Button>
        ) : (
          <Button size="sm" onClick={runSync} disabled={!canSync || sync.isPending}>
            <RefreshCw className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
            {sync.isPending ? 'Wird abgeglichen …' : 'Jetzt synchronisieren'}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirmRevoke(true)}
          disabled={revoke.isPending || connection.status === 'revoked'}
        >
          <Unlink className="h-4 w-4" />
          Trennen
        </Button>
      </div>

      <ConfirmDialog
        open={confirmRevoke}
        onOpenChange={setConfirmRevoke}
        title="Bankverbindung trennen"
        message={
          isFints
            ? `„${connection.bank_label}" trennen? Deine bereits importierten Umsätze bleiben erhalten.`
            : `„${connection.bank_label}" trennen? Die Zustimmung wird bei der Bank widerrufen. Deine bereits importierten Umsätze bleiben erhalten.`
        }
        confirmLabel="Trennen"
        danger
        loading={revoke.isPending}
        onConfirm={doRevoke}
      />

      {isFints ? (
        <Modal
          open={fintsSyncChallenge != null}
          onOpenChange={(open) => {
            if (!open) setFintsSyncChallenge(null);
          }}
          title="TAN-Bestätigung"
          size="sm"
        >
          {fintsSyncChallenge ? (
            <TanWaitPanel
              challengeId={fintsSyncChallenge.id}
              onCompleted={(result) => {
                setFintsSyncChallenge(null);
                toast(result.run ? SYNC_RUN_SENTENCE[result.run.status] : 'TAN bestätigt.', {
                  variant: result.run?.status === 'error' ? 'error' : 'success',
                });
              }}
              onRetry={() => setFintsSyncChallenge(null)}
            />
          ) : null}
        </Modal>
      ) : null}

      {isFints ? (
        <Modal
          open={reauthOpen}
          onOpenChange={(open) => {
            setReauthOpen(open);
            if (!open) {
              setReauthPin('');
              setReauthChallenge(null);
            }
          }}
          title="Bankverbindung erneuern"
          size="sm"
          footer={
            reauthChallenge ? undefined : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setReauthOpen(false)}
                  disabled={fintsReauth.isPending}
                >
                  Abbrechen
                </Button>
                <Button size="sm" onClick={submitFintsReauth} disabled={fintsReauth.isPending}>
                  {fintsReauth.isPending ? 'Wird gesendet …' : 'Bestätigen'}
                </Button>
              </>
            )
          }
        >
          {reauthChallenge ? (
            <TanWaitPanel
              challengeId={reauthChallenge.id}
              onCompleted={() => {
                setReauthChallenge(null);
                setReauthOpen(false);
                toast('Bankverbindung erneuert.', { variant: 'success' });
              }}
              onRetry={() => setReauthChallenge(null)}
            />
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {connection.status === 'error'
                  ? 'Die PIN wurde von der Bank abgelehnt. Bitte gib deine PIN erneut ein.'
                  : 'TAN-Bestätigung erforderlich. Nur bei geänderter PIN neu eingeben, sonst leer lassen.'}
              </p>
              <Field
                label="Neue PIN"
                hint="Leer lassen, um die gespeicherte PIN weiter zu verwenden."
              >
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    value={reauthPin}
                    onChange={(e) => setReauthPin(e.target.value)}
                    autoComplete="off"
                  />
                )}
              </Field>
            </div>
          )}
        </Modal>
      ) : null}
    </div>
  );
}
